import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* BadgeTint — audit #9 rename (2026-05-16). v4 poetic aliases
   (glycine/vert/rose/jaune/bleu) replaced with semantic names matching
   the `--color-{tint}-*` token series. `neutral` retained for the
   default un-tinted state. */
export type BadgeTint = 'neutral' | 'theme' | 'success' | 'error' | 'warning' | 'info';
export type BadgeSize = 'sm' | 'md' | 'lg';
export type BadgeVariant = 'filled' | 'outlined';
export type BadgeContrast = 'auto' | 'none';

const AUTO_CONTRAST_THRESHOLD = 1.25;

/* Bounded rest-prop allow-list (codex Card r1 lesson). The compile-
   time type narrows the API; `pickAllowed` filters at runtime to
   defend against typed-bag spread bypass. */
type AllowedRestKey = 'id' | 'title' | `data-${string}` | `aria-${string}` | 'lang' | 'dir';
type AllowedRest = Partial<Record<AllowedRestKey, unknown>>;
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface BadgeProps extends StyledProps, WithChildren, UnsafeStyleProps, AllowedRest {
  tint?: BadgeTint;
  size?: BadgeSize;
  variant?: BadgeVariant;
  contrast?: BadgeContrast;
  dot?: boolean;
  /* When set, the badge displays the count number; `children` is
     ignored. Counts above `max` display as `{max}+`. */
  count?: number;
  /* Count-overflow threshold; default 99 (compact UIs). Per
     Material 3 / Carbon convention. */
  max?: number;
  /* When `count === 0`, default behavior is to render nothing (no
     DOM) — matches MUI / Material 3. Set `showZero` to render the
     literal "0" instead. */
  showZero?: boolean;
  /* When true, emits `role="status"` + `aria-live="polite"` +
     `aria-atomic="true"` so count changes are announced. Use for
     unread-counter style badges. */
  live?: boolean;
}

function formatCount(count: number, max: number): string {
  return count > max ? `${max}+` : String(count);
}

function parseRgb(value: string): [number, number, number, number] | null {
  const raw = value.trim().toLowerCase();
  if (!raw || raw === 'transparent') return null;
  const rgb = raw.match(/^rgba?\(([^)]+)\)$/);
  if (!rgb) return null;
  const parts = rgb[1]
    .split(/[\s,/]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 3) return null;
  const channels = parts.slice(0, 3).map((part) => {
    const pct = part.endsWith('%');
    const n = Number.parseFloat(part);
    if (!Number.isFinite(n)) return Number.NaN;
    return pct ? Math.round((n / 100) * 255) : n;
  });
  if (channels.some((n) => !Number.isFinite(n))) return null;
  const alpha = parts[3] == null ? 1 : Number.parseFloat(parts[3]);
  return [
    Math.max(0, Math.min(255, channels[0])),
    Math.max(0, Math.min(255, channels[1])),
    Math.max(0, Math.min(255, channels[2])),
    Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1,
  ];
}

function relativeLuminance([r, g, b]: [number, number, number, number]): number {
  const channel = (value: number) => {
    const v = value / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastRatio(a: [number, number, number, number], b: [number, number, number, number]) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

function nearestPaintedBackground(el: HTMLElement): [number, number, number, number] | null {
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const bg = parseRgb(getComputedStyle(cur).backgroundColor);
    if (bg && bg[3] > 0) return bg;
    cur = cur.parentElement;
  }
  if (document.body) {
    const body = parseRgb(getComputedStyle(document.body).backgroundColor);
    if (body && body[3] > 0) return body;
  }
  if (document.documentElement) {
    const root = parseRgb(getComputedStyle(document.documentElement).backgroundColor);
    if (root && root[3] > 0) return root;
  }
  return null;
}

function resolveLayerBackground(el: HTMLElement): [number, number, number, number] | null {
  let cur: HTMLElement | null = el;
  while (cur) {
    const direct = parseRgb(getComputedStyle(cur).getPropertyValue('--layer'));
    if (direct && direct[3] > 0) return direct;
    cur = cur.parentElement;
  }

  const probe = document.createElement('span');
  probe.style.position = 'absolute';
  probe.style.inlineSize = '1px';
  probe.style.blockSize = '1px';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.backgroundColor = 'var(--layer)';
  el.appendChild(probe);
  const bg = parseRgb(getComputedStyle(probe).backgroundColor);
  probe.remove();
  return bg && bg[3] > 0 ? bg : null;
}

function needsAutoContrastRule(el: HTMLElement): boolean {
  const badgeBg = parseRgb(getComputedStyle(el).backgroundColor);
  const surfaceBg = nearestPaintedBackground(el) ?? resolveLayerBackground(el);
  if (!badgeBg || !surfaceBg) return true;
  return contrastRatio(badgeBg, surfaceBg) < AUTO_CONTRAST_THRESHOLD;
}

function setForwardedRef<T>(ref: React.ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  {
    children,
    tint = 'neutral',
    size = 'md',
    variant = 'filled',
    contrast,
    dot = true,
    count,
    max = 99,
    showZero = false,
    live = false,
    className,
    unsafe_style,
    ...rest
  },
  ref,
) {
  const nodeRef = React.useRef<HTMLSpanElement | null>(null);
  const isCountMode = typeof count === 'number';
  const isZeroSuppressed = isCountMode && count === 0 && !showZero;
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.HTMLAttributes<HTMLSpanElement>;

  /* Live-region attributes are emitted together as the canonical
     status-region triplet per WAI-ARIA. Only set when `live` opt-in. */
  const liveProps = live
    ? {
        role: 'status' as const,
        'aria-live': 'polite' as const,
        'aria-atomic': 'true' as const,
      }
    : undefined;

  const content = isCountMode ? formatCount(count, max) : children;
  /* Count mode never shows the dot prefix — the count itself IS the
     visible content. The dot remains for label-style badges. */
  const showDot = dot && !isCountMode;
  const resolvedContrast: BadgeContrast = contrast ?? (tint === 'neutral' ? 'auto' : 'none');
  const canMeasureAutoContrast =
    tint === 'neutral' && variant !== 'outlined' && resolvedContrast === 'auto';
  const [autoRule, setAutoRule] = React.useState(false);
  const setBadgeRef = React.useCallback(
    (node: HTMLSpanElement | null) => {
      nodeRef.current = node;
      setForwardedRef(ref, node);
    },
    [ref],
  );

  React.useEffect(() => {
    if (!canMeasureAutoContrast) {
      setAutoRule(false);
      return;
    }
    const node = nodeRef.current;
    if (!node || typeof window === 'undefined' || typeof getComputedStyle !== 'function') return;
    const update = () => {
      setAutoRule((prev) => {
        const next = needsAutoContrastRule(node);
        return prev === next ? prev : next;
      });
    };
    update();

    if (typeof MutationObserver === 'undefined') return;
    const observed: Element[] = [];
    const observer = new MutationObserver(update);
    let cur: Element | null = node;
    while (cur) {
      observed.push(cur);
      cur = cur.parentElement;
    }
    if (document.documentElement && !observed.includes(document.documentElement)) {
      observed.push(document.documentElement);
    }
    for (const target of observed) {
      observer.observe(target, {
        attributes: true,
        attributeFilter: ['class', 'style', 'data-theme', 'data-density'],
      });
    }
    return () => observer.disconnect();
  }, [canMeasureAutoContrast]);

  /* Suppress the entire badge when count is zero unless explicitly
     told to show it. Matches MUI / Material 3 default behavior.
     EXCEPT when `live` is true: removing the live region between
     0 → 1 transitions means the live region is freshly mounted on
     the 1, and screen readers often don't announce the initial
     contents of a newly-mounted aria-live node (codex round 1).
     Live-mode counters render an SR-only placeholder at zero so
     subsequent count updates re-announce via the persistent node. */
  if (isZeroSuppressed && !live) {
    return null;
  }

  /* SR-only placeholder for live + zero-suppressed: keep the node in
     the DOM (live region persistence — see comment above) but render
     visually invisible while remaining in the accessibility tree.
     The standard clip-rect SR-only recipe. */
  if (isZeroSuppressed) {
    return (
      <span
        ref={setBadgeRef}
        {...restProps}
        {...liveProps}
        className={cx(
          'j-badge',
          `j-badge-${size}`,
          `j-badge-${tint}`,
          `j-badge-${variant}`,
          resolvedContrast === 'auto' && 'j-badge-contrast-auto',
          autoRule && 'j-badge-contrast-rule',
          className,
        )}
        style={{
          /* Visually-hidden but AT-perceivable. Modern recipe: a 1×1
             clip-path inset positioned absolutely, with overflow hidden
             so it occupies no visible space. Avoids the deprecated `clip`
             property (which JSDOM also doesn't serialize reliably). */
          position: 'absolute',
          inlineSize: 1,
          blockSize: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          border: 0,
          ...unsafe_style,
        }}
      />
    );
  }

  return (
    <span
      ref={setBadgeRef}
      {...restProps}
      {...liveProps}
      className={cx(
        'j-badge',
        `j-badge-${size}`,
        `j-badge-${tint}`,
        `j-badge-${variant}`,
        resolvedContrast === 'auto' && 'j-badge-contrast-auto',
        autoRule && 'j-badge-contrast-rule',
        className,
      )}
      style={unsafe_style}
    >
      {showDot && <span className="j-badge-dot" />}
      {content}
    </span>
  );
});
